/**
 * VerifyEmail — /verify?token=...   and   /verify?change=1&token=...
 *
 * Two jobs, one page, because they're the same journey: a link arrives by
 * email, the token in it gets handed to the backend, and the person is told
 * whether it worked.
 *
 *   No `change` flag → confirming a new signup.
 *   change=1         → confirming a NEW email address for an existing account
 *                      (Settings → Account → Change email). Deliberately works
 *                      logged out, since people click links in whatever mail
 *                      app they're in, not necessarily a signed-in browser.
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { authApi, accountApi } from '../services/api'
import { JaiMark, JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const isEmailChange = searchParams.get('change') === '1'

  const [state, setState] = useState('loading') // 'loading' | 'success' | 'error' | 'no-token'
  const [errorMsg, setErrorMsg] = useState('')
  const [newEmail, setNewEmail] = useState('')

  useEffect(() => {
    if (!token) {
      setState('no-token')
      return
    }
    verify()
  }, [token])

  const verify = async () => {
    setState('loading')
    try {
      if (isEmailChange) {
        const { data } = await accountApi.confirmEmailChange(token)
        setNewEmail(data.email || '')
      } else {
        await authApi.verifyEmail(token)
      }
      setState('success')
    } catch (err) {
      const msg = err.response?.data?.error || 'Verification failed. The link may have expired.'
      setErrorMsg(msg)
      setState('error')
    }
  }

  const copy = isEmailChange
    ? {
        loading: 'Confirming your new email…',
        successTitle: 'Email address updated',
        successBody: newEmail
          ? <>Your JAI account now uses <strong>{newEmail}</strong>. Use it next time you sign in.</>
          : 'Your JAI account email has been updated. Use it next time you sign in.',
        errorTitle: 'Link expired',
        errorHint: 'Confirmation links are valid for 24 hours. Start the change again from Settings → Account to get a new one.',
      }
    : {
        loading: 'Verifying your email…',
        successTitle: 'Email verified',
        successBody: 'Your account is active. Sign in to get started.',
        errorTitle: 'Link expired',
        errorHint: 'Verification links are valid for 24 hours. Try signing up again to receive a new link.',
      }

  return (
    <div style={{ minHeight: 'calc(100vh / var(--jai-zoom))', background: JAI_COLORS.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <JaiMark size={60} />
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: JAI_COLORS.navy, marginBottom: 8, fontFamily: JAI_FONTS.tight }}>JAI</h1>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', borderRadius: 16, padding: 32, border: `1px solid ${JAI_COLORS.hair}`, textAlign: 'center', boxShadow: '0 4px 32px rgba(58,124,255,0.08)' }}>

          {/* Loading */}
          {state === 'loading' && (
            <>
              <div style={{ width: 56, height: 56, background: `${JAI_COLORS.blue}15`, border: `1px solid ${JAI_COLORS.blue}30`, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28, fontWeight: 800, color: JAI_COLORS.blue, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
                K
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: JAI_COLORS.navy, marginBottom: 8, fontFamily: JAI_FONTS.tight }}>{copy.loading}</h2>
              <p style={{ color: JAI_COLORS.mute, fontSize: 14 }}>Just a moment.</p>
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
            </>
          )}

          {/* Success */}
          {state === 'success' && (
            <>
              <div style={{ width: 56, height: 56, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28, color: '#22c55e' }}>
                ✓
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: JAI_COLORS.navy, marginBottom: 8, fontFamily: JAI_FONTS.tight }}>{copy.successTitle}</h2>
              <p style={{ color: JAI_COLORS.mute, fontSize: 14, marginBottom: 24 }}>
                {copy.successBody}
              </p>
              <Link
                to="/login"
                style={{ display: 'inline-block', background: `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, #1E5BD6 100%)`, color: 'white', fontWeight: 600, borderRadius: 8, padding: '11px 24px', fontSize: 13, textDecoration: 'none', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight, boxShadow: `0 4px 16px ${JAI_COLORS.blue}40` }}
                onMouseEnter={(e) => { e.style.transform = 'translateY(-1px)'; e.style.boxShadow = `0 6px 20px ${JAI_COLORS.blue}50` }}
                onMouseLeave={(e) => { e.style.transform = 'translateY(0)'; e.style.boxShadow = `0 4px 16px ${JAI_COLORS.blue}40` }}
              >
                Sign in →
              </Link>
            </>
          )}

          {/* Error */}
          {state === 'error' && (
            <>
              <div style={{ width: 56, height: 56, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28, color: '#ef4444' }}>
                ✕
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: JAI_COLORS.navy, marginBottom: 8, fontFamily: JAI_FONTS.tight }}>{copy.errorTitle}</h2>
              <p style={{ color: JAI_COLORS.mute, fontSize: 14, marginBottom: 16 }}>{errorMsg}</p>
              <p style={{ color: JAI_COLORS.mute, fontSize: 12, marginBottom: 24, fontStyle: 'italic' }}>
                {copy.errorHint}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* An email change is fixed by retrying from Settings, not by
                    signing up again — that would be the wrong door entirely. */}
                <Link
                  to={isEmailChange ? '/login' : '/signup'}
                  style={{ display: 'inline-block', background: `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, #1E5BD6 100%)`, color: 'white', fontWeight: 600, borderRadius: 8, padding: '11px 24px', fontSize: 13, textDecoration: 'none', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight, boxShadow: `0 4px 16px ${JAI_COLORS.blue}40` }}
                  onMouseEnter={(e) => { e.style.transform = 'translateY(-1px)'; e.style.boxShadow = `0 6px 20px ${JAI_COLORS.blue}50` }}
                  onMouseLeave={(e) => { e.style.transform = 'translateY(0)'; e.style.boxShadow = `0 4px 16px ${JAI_COLORS.blue}40` }}
                >
                  {isEmailChange ? 'Sign in' : 'Sign up again'}
                </Link>
                {!isEmailChange && (
                  <Link
                    to="/login"
                    style={{ color: JAI_COLORS.blue, fontWeight: 500, textDecoration: 'none', fontSize: 14, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = '#1E5BD6'}
                    onMouseLeave={e => e.target.style.color = JAI_COLORS.blue}
                  >
                    ← Back to sign in
                  </Link>
                )}
              </div>
            </>
          )}

          {/* No token in URL */}
          {state === 'no-token' && (
            <>
              <div style={{ width: 56, height: 56, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28, color: '#d97706' }}>
                !
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: JAI_COLORS.navy, marginBottom: 8, fontFamily: JAI_FONTS.tight }}>Invalid link</h2>
              <p style={{ color: JAI_COLORS.mute, fontSize: 14, marginBottom: 24 }}>
                This verification link is missing its token. Try clicking the link in your email again, or sign up for a new link.
              </p>
              <Link
                to="/login"
                style={{ color: JAI_COLORS.blue, fontWeight: 500, textDecoration: 'none', fontSize: 14, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = '#1E5BD6'}
                onMouseLeave={e => e.target.style.color = JAI_COLORS.blue}
              >
                ← Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
